import React, { useEffect, useContext, useRef } from "react";
import { accountService } from "../../services/accountService";
import LogicContext from "../../Context/LogicContext";
import {
  SetPaymentMadeAtom,
  GetPaymentReceiveAtom
} from "../../store/paymentReceivedAtom";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdRefresh } from "react-icons/io";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import { useForm } from "react-hook-form";
import moment from "moment";
import { formatCustomDate } from "../../helpers/formatCustomDate";
import Papa from "papaparse"; // ← for reliable CSV export
import StatusBadge from "../../generals/StatusBadge";

const columns = [
  {
    title: 'Reference',
    key: 'Ref_No',
    dataIndex: 'Ref_No',
    sorter: (a, b) => (a?.Ref_No ?? '') - (b?.Ref_No ?? '')
  },
  {
    title: 'Date',
    dataIndex: 'Transaction_Date',
    className: "text-primary font-bold",
    sorter: (a, b) => (a?.Transaction_Date ?? '').localeCompare(b?.Transaction_Date ?? ''),
    render: (record) => <span>{formatCustomDate(record)}</span>
  },
  {
    title: "Customer Name",
    dataIndex: "Customer_Full_Name",
    sorter: (a, b) => (a?.Customer_Full_Name ?? '').localeCompare(b?.Customer_Full_Name ?? '')
  },
  {
    title: "Customer Mobile",
    dataIndex: "Customer_Mobile_No",
    sorter: (a, b) => (a?.Customer_Mobile_No ?? '').localeCompare(b?.Customer_Mobile_No ?? '')
  },
  {
    title: "Email",
    dataIndex: "Customer_Email_Address",
    sorter: (a, b) => (a?.Customer_Email_Address ?? '').localeCompare(b?.Customer_Email_Address ?? '')
  },
  {
    title: "Deposit To",
    dataIndex: "Deposit_Method_Name",
    sorter: (a, b) => (a?.Deposit_Method_Name ?? '').localeCompare(b?.Deposit_Method_Name ?? '')
  },
  {
    title: "Payment Amount",
    dataIndex: "Deposit_Amount",
    sorter: (a, b) => (a?.Deposit_Amount ?? '') - (b?.Deposit_Amount ?? ''),
    render: (record) => <span>{formatCustomNumber(record)}</span>
  },
  {
    title: "Charge Amount",
    dataIndex: "Charge_Amount",
    sorter: (a, b) => (a?.Charge_Amount ?? '') - (b?.Charge_Amount ?? ''),
    render: (record) => <span>{formatCustomNumber(record)}</span>
  },
  {
    title: 'Deposit status',
    dataIndex: 'Deposit_Status',
    sorter: (a, b) => (a?.Deposit_Status ?? '').localeCompare(b?.Deposit_Status ?? ''),
    render: (record, row) => (
      <StatusBadge statusId={row?.Payment_Status_Id} statusName={record} />
    ),
  },
];

function PaymentReceivedPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const logicContext = useContext(LogicContext);

  const setPaymentMadeAtom = SetPaymentMadeAtom();
  const getPaymentReceiveAtom = GetPaymentReceiveAtom();

  const [tableData, setTableData] = useState([]);
  const [itemSelected, setItemSelected] = useState([]);
  const [searchText, setSearchText] = useState("");

  const { register, getValues } = useForm();

  const isMounted = useRef(false);

  useEffect(() => {
    document.title = "Payment Receive";
    isMounted.current = true;

    if (!getPaymentReceiveAtom.deposit_method?.length) {
      accountService.getPaymentReceiveList().then((response) => {
        if (response?.data?.deposit_info?.length) {
          setTableData(response.data.deposit_info);
          setPaymentMadeAtom(response.data);
        }
      });
    } else {
      setTableData(getPaymentReceiveAtom.deposit_info);
    }

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPaymentReceiveAtom.deposit_info?.length]);

  useEffect(() => {
    if (sessionStorage.getItem("reloadPage") === "true") {
      sessionStorage.removeItem("reloadPage");
      getPaymentListApi();
    }
  }, []);

  const getPaymentListApi = async () => {
    try {
      const response = await accountService.getPaymentReceiveList();
      if (response?.data?.deposit_info?.length) {
        setTableData(response.data.deposit_info);
        setPaymentMadeAtom(response.data);
      }
    } catch (error) {
      logicContext.showToast("connection error getting payment data", "error");
    }
  };

  function selectedTableItems(itemList) {
    setItemSelected(itemList);
  }

  function addNewInvoice() {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  }

  const searchByDate = async () => {
    const formData = getValues();
    const response = await accountService.paymentReceiveByDate(formData);
    if (response?.data?.length) {
      setTableData(response?.data);
    } else {
      logicContext.showToast("No result found", "");
      setTableData([]);
    }
  };

  const handleTableSearch = (text) => {
    setSearchText(text);
    if (text.trim() === "") {
      setTableData(getPaymentReceiveAtom.deposit_info || []);
      return;
    }

    const searchTerm = text.toLowerCase();
    const filtered = (getPaymentReceiveAtom.deposit_info || []).filter(credit =>
      Object.values(credit).some(value =>
        value?.toString().toLowerCase().includes(searchTerm)
      )
    );
    setTableData(filtered);
  };

  const onClearInput = () => {
    setSearchText("");
    setTableData(getPaymentReceiveAtom.deposit_info || []);
  };

  // ────────────────────────────────────────────────
  //               EXPORT FUNCTION
  // ────────────────────────────────────────────────
  const exportToCSV = () => {
    if (!tableData?.length) {
      logicContext.showToast("No data to export", "warning");
      return;
    }

    const headers = columns.map(col => col.title);
    const keys = columns.map(col => col.dataIndex);

    const dataForExport = tableData.map(row => {
      const obj = {};
      keys.forEach((key, index) => {
        let value = row[key] ?? '';

        // Match table formatting
        if (key === 'Transaction_Date') {
          value = formatCustomDate(value);
        } else if (key === 'Deposit_Amount' || key === 'Charge_Amount') {
          value = formatCustomNumber(value);
        }

        obj[headers[index]] = value;
      });
      return obj;
    });

    const csv = Papa.unparse(dataForExport, {
      header: true,
      delimiter: ","
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `payment_received_${moment().format("YYYY-MM-DD_HHmmss")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    logicContext.showToast("Exported successfully", "success");
  };

  const paginationOptions = {
    pageSize: 10,
    showSizeChanger: true,
    total: tableData.length,
  };

  function handleRowClick(payment) {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${payment?.Ref_No}`);
  }

  return (
    <>
      <section className="md:flex items-center justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-2">
          <Tooltip title="Reload">
            <div
              onClick={() => getPaymentListApi()}
              className="h-fit w-fit p-2 rounded-full cursor-pointer hover:bg-gray-200 active:bg-gray-300"
            >
              <IoMdRefresh className="h-[20px] w-[20px] text-gray-600" />
            </div>
          </Tooltip>
        </div>

        {/* Date range filter */}
        <div className="flex flex-row items-end space-x-2">
          <div>
            <span className="ml-1 text-sm">From Date:</span>
            <input
              defaultValue={moment().format("YYYY-MM-DD")}
              {...register("from_date")}
              className="bg-white ring-1 ring-gray-200 w-full pl-3 rounded outline-none"
              type="date"
            />
          </div>
          <div>
            <span className="ml-1 text-sm">To Date:</span>
            <input
              {...register("to_date")}
              defaultValue={moment().format("YYYY-MM-DD")}
              className="bg-white ring-1 ring-gray-200 w-full pl-3 rounded outline-none"
              type="date"
            />
          </div>
          <div>
            <button
              onClick={searchByDate}
              className="text-sm text-white px-3 bg-blue-500 rounded py-2.5"
            >
              Search
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={exportToCSV}
            className="font-[Poppins] font-semibold py-2 px-4 rounded text-sm border border-gray-300 hover:bg-gray-100 transition-colors"
          >
            EXPORT
          </button>

          <button
            onClick={addNewInvoice}
            className="bg-buttonColor font-[Poppins] font-semibold py-2 px-3 rounded text-white text-sm"
          >
            ADD NEW
          </button>
        </div>
      </section>

      {/* Table search */}
      <div className="flex justify-center mt-4">
        <div className="flex flex-col w-full md:w-1/3">
          <input
            type="text"
            placeholder="Search in table..."
            value={searchText}
            onChange={(e) => handleTableSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && searchText === "") {
                onClearInput();
              }
            }}
            className="bg-white ring-1 ring-gray-200 w-full pl-3 py-2 rounded outline-none focus:ring-blue-300"
          />
        </div>
      </div>

      <section className="mt-5">
        <GeneralCustomTable
          tableHeadList={columns}
          tableDataList={tableData}
          allowCheckBox={true}
          setSelectedRows={selectedTableItems}
          paginationOptions={paginationOptions}
          rowKey={(record) => record.Payment_Ref_No}
          handleRowClick={handleRowClick}
        />
      </section>
    </>
  );
}

export default PaymentReceivedPage;